// src/pages/projects/ProjectCompanyRepresentativeModal.tsx
import React, { useState, useEffect } from 'react';
import type { ProjectCompanyItem } from 'src/features/projects/companiesApi';

export interface ProjectCompanyRepresentativeModalProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    projectCompany: ProjectCompanyItem | null;
    onSaved: (updated: ProjectCompanyItem) => void;
    onSubmit: (args: {
        projectId: string;
        projectCompanyId: string;
        email: string;
        name?: string;
    }) => Promise<ProjectCompanyItem>;
}

const ProjectCompanyRepresentativeModal: React.FC<ProjectCompanyRepresentativeModalProps> = ({
    open,
    onClose,
    projectId,
    projectCompany,
    onSaved,
    onSubmit,
}) => {
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    // 모달이 열릴 때마다 현재 ProjectCompany 상태에 맞게 값 세팅
    useEffect(() => {
        if (!open || !projectCompany) return;

        setErr(null);
        // 이미 대표자가 있다면 기본값으로 채워 준다
        if (projectCompany.representativeMember) {
            setEmail(projectCompany.representativeMember.user.email);
            setName(projectCompany.representativeMember.user.name || '');
        } else {
            setEmail('');
            setName('');
        }
    }, [open, projectCompany]);

    if (!open || !projectCompany) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim()) {
            setErr('대표자의 이메일을 입력해 주세요.');
            return;
        }

        try {
            setSubmitting(true);
            setErr(null);

            const updated = await onSubmit({
                projectId,
                projectCompanyId: projectCompany.id,
                email: email.trim(),
                name: name.trim() || undefined,
            });

            onSaved(updated);
            onClose();
        } catch (error: any) {
            const serverMsg = error?.response?.data?.error?.message;
            setErr(serverMsg ?? '대표자 저장에 실패했습니다.');
        } finally {
            setSubmitting(false);
        }
    };

    const companyName = projectCompany.company.name;
    const companyType = projectCompany.company.type;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                {/* 헤더 */}
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">회사 대표자 설정</h2>
                        <p className="mt-0.5 text-xs text-gray-500">
                            이 프로젝트에서 아래 회사의 담당자(대표자)를 지정합니다.
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600">
                        닫기
                    </button>
                </div>

                {/* 본문 */}
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {/* 회사 정보 */}
                    <div className="rounded-xl bg-gray-50 px-3 py-2 text-xs text-gray-700">
                        <div className="font-medium text-gray-900">
                            {companyName}{' '}
                            {companyType && <span className="text-[10px] text-gray-400">({companyType})</span>}
                        </div>
                        <div className="mt-1 text-[11px] text-gray-500">
                            대표자는 이 회사(Partner)의 소속 사용자여야 합니다. 이메일이 아직 등록되지 않았다면, 해당
                            이메일로 사용자를 생성한 뒤 회사/프로젝트 멤버로 연결됩니다.
                        </div>
                    </div>

                    {/* 에러 메시지 */}
                    {err && (
                        <div className="rounded-md border border-rose-100 bg-rose-50 px-3 py-2 text-[11px] text-rose-700">
                            {err}
                        </div>
                    )}

                    {/* 이메일 입력 */}
                    <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                            대표자 이메일
                            <span className="ml-0.5 text-rose-500">*</span>
                        </label>
                        <input
                            type="email"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
                            placeholder="representative@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={submitting}
                        />
                    </div>

                    {/* 이름(선택) */}
                    <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">대표자 이름 (선택)</label>
                        <input
                            type="text"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
                            placeholder="이름을 입력해 주세요 (선택)"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={submitting}
                        />
                    </div>

                    {/* 버튼 영역 */}
                    <div className="mt-2 flex justify-end gap-2">
                        <button
                            type="button"
                            className="px-3 py-1.5 rounded-full border border-gray-300 bg-white text-xs text-gray-700 hover:bg-gray-50"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            취소
                        </button>
                        <button
                            type="submit"
                            className="px-3 py-1.5 rounded-full bg-sky-700 text-white text-xs hover:bg-sky-800 disabled:opacity-60"
                            disabled={submitting}
                        >
                            {submitting ? '저장 중…' : '대표자 저장'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProjectCompanyRepresentativeModal;
